import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BadgeCheck, Bookmark, Eye, EyeOff, HandCoins, MapPin, Scale, UserRound } from "lucide-react";
import { api, media } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useVisitor } from "@/lib/visitor";
import StoriesRail from "@/components/lux/StoriesRail";
import ResultsGrid from "@/components/lux/ResultsGrid";
import SafetyCallout from "@/components/lux/SafetyCallout";
import { WhatsAppIcon } from "@/components/lux/Icons";

const PILLARS = [
  { icon: UserRound, title: "One verified person, one profile", text: "Identity and age checked before publishing, and re-confirmed with a selfie every 90 days. The badge shows exactly how recently." },
  { icon: Eye, title: "Every word and photo reviewed", text: "Moderators approve every profile, edit and story before it appears. Nothing explicit, no stolen photos, no agencies." },
  { icon: HandCoins, title: "No bookings. No deposits.", text: "LUX is a directory only. We never take payments, so anyone asking you to pay through LUX is attempting fraud." },
  { icon: WhatsAppIcon, title: "WhatsApp on every profile", text: "Reach advertisers directly on WhatsApp with a respectful, pre-written first message. No in-app chat, no middlemen." },
  { icon: EyeOff, title: "Discreet by design", text: "Blur every photo while you browse, keep a shortlist on your own device and leave instantly with Quick exit." },
  { icon: Scale, title: "Fair ranking, no paid placement", text: "Results are ordered by availability today and verification freshness. Nobody can buy a higher position." },
];

function CityTile({ city, large }) {
  const { blur } = useVisitor();
  return (
    <Link to={`/${city.slug}`} className={`group relative block overflow-hidden rounded-[12px] border border-lux-border bg-lux-surface hover:border-lux-silver/45 ${large ? "sm:col-span-2 lg:col-span-1 lg:row-span-2" : ""}`} data-testid={`home-city-tile-${city.slug}`}>
      <div className={`lux-card-media overflow-hidden ${large ? "aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[420px]" : "aspect-[16/10]"}`}>
        {city.image && <img src={media(city.image)} alt="" loading="lazy" className={`h-full w-full object-cover opacity-80 ${blur ? "blur-xl" : ""}`} />}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
        {city.is_default && <span className="lux-label text-lux-silverBright">Main city</span>}
        <h3 className={`font-serif leading-none ${large ? "mt-2 text-4xl sm:text-5xl" : "mt-2 text-3xl"}`}>{city.name}</h3>
        {city.tagline && <p className="mt-2 text-sm text-lux-text/75">{city.tagline}</p>}
        <p className="mt-3 flex items-center gap-1.5 text-xs text-lux-silver">
          <MapPin className="h-3 w-3" aria-hidden="true" /> {city.count} verified advertiser{city.count === 1 ? "" : "s"} · {city.areas.length} areas
        </p>
        <div className="mt-3 hidden flex-wrap gap-1.5 sm:flex">
          {city.areas.slice(0, large ? 6 : 3).map((a) => <span key={a.slug} className="rounded-full border border-white/15 px-2 py-0.5 text-[11px] text-lux-text/80">{a.name}</span>)}
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const { data, isLoading } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/public/home")).data });
  const { blur, saved } = useVisitor();
  useSeo(data?.seo || { title: "LUX - Verified independent advertisers in London, Birmingham and Manchester" });
  const main = data?.city;
  const cities = data?.cities || [];
  return (
    <div>
      <section className="lux-noise relative isolate overflow-hidden border-b border-lux-border" data-testid="home-hero">
        {data?.hero && <img src={media(data.hero)} alt="" className={`absolute inset-0 -z-10 h-full w-full object-cover opacity-60 ${blur ? "blur-xl" : ""}`} />}
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-40 bg-gradient-to-t from-[#0B0B0C] to-transparent" />
        <div className="mx-auto max-w-6xl px-4 pb-20 pt-20 sm:px-6 sm:pb-28 sm:pt-28 lg:px-8 lg:pb-36 lg:pt-36">
          <p className="lux-label animate-lux-fade-up" data-testid="home-hero-cities-label">{cities.length ? cities.map((c) => c.name).join(" · ") : "London · Birmingham · Manchester"}</p>
          <h1 className="mt-5 max-w-3xl font-serif text-5xl font-normal leading-[1.02] text-lux-text animate-lux-fade-up sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }}>
            Discretion,<br /><em className="font-light text-lux-silverBright">verified.</em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-lux-text/80 animate-lux-fade-up sm:text-lg" style={{ animationDelay: "160ms" }}>
            A calm, moderated directory of independent adults in London, Birmingham and Manchester. Every advertiser is one verified person with one profile - and every photo is reviewed before it appears.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 animate-lux-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to={`/${main?.slug || "london"}`} className="lux-btn lux-btn-primary px-7" data-testid="home-hero-browse-main-city-button">
              Browse {main?.name || "London"} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/safety" className="lux-btn lux-btn-secondary px-7" data-testid="home-hero-safety-link">How verification works</Link>
          </div>
          {data && (
            <p className="mt-10 flex items-center gap-2 text-xs text-lux-muted">
              <BadgeCheck className="h-4 w-4 text-lux-silver" aria-hidden="true" />
              <span data-testid="home-live-count">{data.total} verified advertisers across {cities.length} cities</span>
            </p>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <section className="mt-14" aria-labelledby="cities-h" data-testid="home-cities">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="lux-label">Choose your city</p>
              <h2 id="cities-h" className="mt-2 font-serif text-4xl">Three cities, one standard</h2>
            </div>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-2">
            {isLoading && Array.from({ length: 3 }).map((_, i) => <div key={i} className="aspect-[16/10] animate-pulse rounded-[12px] border border-lux-border bg-lux-surface" />)}
            {cities.map((c, i) => <CityTile key={c.slug} city={c} large={i === 0} />)}
          </div>
        </section>

        {data?.stories?.length > 0 && (
          <div className="mt-14">
            <StoriesRail groups={data.stories} subtitle="From verified advertisers in all three cities" />
          </div>
        )}

        <section className="mt-20" aria-labelledby="featured-h">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <p className="lux-label">Available today &amp; recently verified</p>
              <h2 id="featured-h" className="mt-2 font-serif text-4xl">Independent advertisers</h2>
            </div>
            <Link to={`/${main?.slug || "london"}/browse`} className="lux-btn lux-btn-secondary lux-btn-sm" data-testid="home-view-all-button">Browse {main?.name || "London"} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <ResultsGrid cards={data?.featured} loading={isLoading} showCity />
        </section>

        <section className="mt-20" aria-labelledby="different-h" data-testid="home-differentiators">
          <p className="lux-label">Why LUX is different</p>
          <h2 id="different-h" className="mt-2 font-serif text-4xl">Built for trust and discretion</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="lux-card p-6">
                <Icon className="h-5 w-5 text-lux-silver" aria-hidden="true" />
                <h3 className="mt-5 font-serif text-2xl leading-tight">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-lux-muted">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-[12px] border border-lux-border bg-lux-surface2 p-4 text-sm text-lux-muted">
            <Bookmark className="h-4 w-4 text-lux-silver" aria-hidden="true" />
            <span>Your shortlist stays on this device - LUX keeps no record of visitors.</span>
            <Link to="/saved" className="lux-link ml-auto" data-testid="home-saved-link">{saved.length ? `${saved.length} saved` : "Open shortlist"}</Link>
          </div>
        </section>

        <div className="mt-20">
          <SafetyCallout />
        </div>
      </div>
    </div>
  );
}
